import { object, string } from 'yup'
import { EditChargingCardFormData, EMobilityChargingCardsDataModel } from 'src/Libs/e-mobility/emobility-charging-cards'
import { ChargeCardStatus } from 'src/Libs/e-mobility/charging-sessions'
import { useCustomTranslation } from '@e3dc-react/shell/Hooks/useCustomTranslation'
import { FormInitializerReturnType, numberFromStringSchema } from 'src/Configuration/validation'

/**
 * Initializes the form for editing an e-mobility charging card.
 * @param model - EMobilityChargingCardsDataModel
 * @returns initialValues and validationSchema for EditChargingCardFormData form
 */
export const useEmobilityChargingCardEditorFormInitializer = (
  model: EMobilityChargingCardsDataModel
): FormInitializerReturnType<EditChargingCardFormData, EditChargingCardFormData> => {
  const { t } = useCustomTranslation()
  const numberFromStringSchemaWithTranslation = numberFromStringSchema(t)

  const initialValues = {
    rfid: model.rfid,
    chargingCardName: model.name ?? '',
    vehicleName: model.vehicleName ?? '',
    chargingCardDescription: model.description ?? '',
    vehicleDescription: model.vehicleDescription ?? '',
    status: model.status,
    licensePlate: model.licensePlate ?? '',
    avgConsumption: model.avgConsumption?.toString() ?? '',
    batteryCapacity: model.batteryCapacity?.toString() ?? '',
  }

  const validationSchema = object().shape({
    batteryCapacity: numberFromStringSchemaWithTranslation()
      .test('batteryCapacity', t('emobility.chargingCards.edit.two-decimal-allowed'), value => {
        if (value) {
          const decimalPlaces = value.toString().split('.')[1]?.length ?? 0
          return decimalPlaces < 3
        }

        return true
      })
      .required(),
    avgConsumption: numberFromStringSchemaWithTranslation()
      .test('avgConsumption', t('emobility.chargingCards.edit.two-decimal-allowed'), value => {
        if (value) {
          const decimalPlaces = value.toString().split('.')[1]?.length ?? 0
          return decimalPlaces < 3
        }

        return true
      })
      .required(),
    rfid: string().required(),
    status: string<ChargeCardStatus>().required(),
    chargingCardName: string(),
    chargingCardDescription: string(),
    vehicleName: string(),
    licensePlate: string(),
    vehicleDescription: string(),
  })

  return { validationSchema, initialValues }
}
